import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../config/axios";
import { errorHandler } from "../../../helpers/handlers";

const initialState = {
  crawlRuns: [],
  schoolIdFilter: "",
  crawlLoading: false,
  coverage: {
    schools: [],
    fields: { critical: [], important: [] },
    note: "",
  },
  coverageLoading: false,
  reports: [],
  reportsLastDocId: null,
  reportsHasMore: false,
  reportsLoading: false,
  promoteLoadingId: null,
  error: null,
};

const slice = createSlice({
  name: "crawler",
  initialState,
  reducers: {
    setCrawlSchoolFilter: (state, action) => {
      state.schoolIdFilter = action.payload || "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCrawlRuns.pending, (state) => {
        state.crawlLoading = true;
        state.error = null;
      })
      .addCase(fetchCrawlRuns.fulfilled, (state, action) => {
        state.crawlLoading = false;
        state.crawlRuns = action.payload.items || [];
      })
      .addCase(fetchCrawlRuns.rejected, (state, action) => {
        state.crawlLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchCoverage.pending, (state) => {
        state.coverageLoading = true;
        state.error = null;
      })
      .addCase(fetchCoverage.fulfilled, (state, action) => {
        state.coverageLoading = false;
        state.coverage = {
          schools: action.payload.schools || [],
          fields: action.payload.fields || { critical: [], important: [] },
          note: action.payload.note || "",
        };
      })
      .addCase(fetchCoverage.rejected, (state, action) => {
        state.coverageLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchReports.pending, (state, action) => {
        state.reportsLoading = true;
        state.error = null;
        if (!action.meta.arg?.lastDocId) {
          state.reports = [];
          state.reportsLastDocId = null;
          state.reportsHasMore = false;
        }
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        const { items, lastDocId, reset } = action.payload;
        if (reset) state.reports = [];
        const incoming = items || [];
        state.reports = [
          ...state.reports.filter(
            (existing) => !incoming.some((item) => item.id === existing.id)
          ),
          ...incoming,
        ];
        state.reportsLastDocId = lastDocId || null;
        state.reportsHasMore = incoming.length >= 25;
        state.reportsLoading = false;
      })
      .addCase(fetchReports.rejected, (state, action) => {
        state.reportsLoading = false;
        state.error = action.payload;
      })
      .addCase(promoteReport.pending, (state, action) => {
        state.promoteLoadingId = action.meta.arg?.id || null;
        state.error = null;
      })
      .addCase(promoteReport.fulfilled, (state, action) => {
        state.promoteLoadingId = null;
        const { id, changeSetId, status } = action.payload;
        state.reports = state.reports.map((r) =>
          r.id === id
            ? { ...r, status: status || "promoted", changeSetId }
            : r
        );
      })
      .addCase(promoteReport.rejected, (state, action) => {
        state.promoteLoadingId = null;
        state.error = action.payload;
      });
  },
});

export const { setCrawlSchoolFilter } = slice.actions;

export const fetchCrawlRuns = createAsyncThunk(
  "crawler/crawlRuns",
  async ({ schoolId = "", limit = 25 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (schoolId) params.set("schoolId", schoolId);
      const response = await api.get(`/admin/crawl-runs?${params.toString()}`);
      return response.data.payload || { items: [] };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(
        error.response?.data?.errors?.global || error.message
      );
    }
  }
);

export const fetchCoverage = createAsyncThunk(
  "crawler/coverage",
  async ({ limit = 20, scope = "both" } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        limit: String(limit),
        scope: String(scope || "both"),
      });
      const response = await api.get(`/admin/coverage?${params.toString()}`);
      return response.data.payload || {};
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(
        error.response?.data?.errors?.global || error.message
      );
    }
  }
);

export const fetchReports = createAsyncThunk(
  "crawler/reports",
  async ({ lastDocId = "", limit = 25, status = "" } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (lastDocId) params.set("lastDocId", lastDocId);
      if (status) params.set("status", status);
      const response = await api.get(`/admin/reports?${params.toString()}`);
      const payload = response.data.payload || {};
      return {
        items: payload.items || [],
        lastDocId: payload.lastDocId || null,
        reset: !lastDocId,
      };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(
        error.response?.data?.errors?.global || error.message
      );
    }
  }
);

export const promoteReport = createAsyncThunk(
  "crawler/promoteReport",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/reports/${id}/promote`, {});
      const payload = response.data.payload || {};
      return {
        id,
        changeSetId:
          payload.changeSetId ||
          payload.changeSet?.id ||
          payload.report?.changeSetId ||
          null,
        status: payload.report?.status || payload.status || "promoted",
      };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.errors?.global ||
          error.message
      );
    }
  }
);

export default slice.reducer;
